
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, User, CreditCard, Shield, Truck, Lock, Plus, Check, X, Tag, Mail, Gift, Banknote } from "lucide-react";
import { useCartStore } from "@/lib/store";
import { useAuthStore } from "@/lib/store";
import { paymentsApi, addressesApi, couponsApi } from "@/lib/api";
import CustomDropdown from "@/components/admin/CustomDropdown";
import toast from "react-hot-toast";

// Declare Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getTotal, getItemCount, clearCart } = useCartStore();
  const { token, user } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Addresses state
  const [savedAddresses, setSavedAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);

  // Helper function to remove +91 prefix from phone number
  const removePhonePrefix = (phone: string | undefined | null): string => {
    if (!phone) return "";
    // Remove +91 prefix if present
    return phone.startsWith("+91") ? phone.substring(3) : phone;
  };

  // Form state
  const [formData, setFormData] = useState({
    customer_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : "",
    customer_email: user?.email || "",
    customer_phone: removePhonePrefix(user?.phone),
    delivery_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : "",
    delivery_phone: removePhonePrefix(user?.phone),
    delivery_address_line1: "",
    delivery_address_line2: "",
    delivery_city: "",
    delivery_state: "",
    delivery_pincode: "",
    delivery_country: "India",
    is_gift: false,
    gift_message: "",
  });

  // Payment method: "razorpay" | "cod"
  const [paymentMethod, setPaymentMethod] = useState<"razorpay" | "cod">("razorpay");

  // Coupon state
  const [availableCoupons, setAvailableCoupons] = useState<any[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  const subtotal = getTotal();
  const shipping = subtotal > 500 ? 0 : 100;
  const discountAmount = couponDiscount;
  const taxableAmount = Math.max(0, subtotal - discountAmount);
  const total = taxableAmount + shipping;

  // Fetch saved addresses and coupons
  useEffect(() => {
    if (token && user) {
      fetchAddresses();
      fetchCoupons();
    }
  }, [token, user]);

  // Redirect if not logged in or cart is empty
  useEffect(() => {
    // Don't run redirect logic if payment was just completed
    if (paymentCompleted) {
      return;
    }

    if (!token || !user) {
      toast.error("Please login to proceed with checkout");
      navigate("/cart");
      return;
    }

    if (items.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
      return;
    }
  }, [token, user, items, navigate, paymentCompleted]);

  const fetchAddresses = async () => {
    try {
      const response = await addressesApi.getAll();
      setSavedAddresses(response.addresses || []);
      // Select default address if available
      const defaultAddress = response.addresses?.find((addr: any) => addr.is_default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
        setFormData((prev) => ({
          ...prev,
          customer_name: defaultAddress.name || prev.customer_name,
          customer_phone: removePhonePrefix(defaultAddress.phone) || prev.customer_phone,
          delivery_name: defaultAddress.name,
          delivery_phone: removePhonePrefix(defaultAddress.phone),
          delivery_address_line1: defaultAddress.address_line1,
          delivery_address_line2: defaultAddress.address_line2 || "",
          delivery_city: defaultAddress.city,
          delivery_state: defaultAddress.state,
          delivery_pincode: defaultAddress.pincode,
          delivery_country: defaultAddress.country,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
    }
  };

  const handleSelectAddress = (address: any) => {
    setSelectedAddressId(address.id);
    setShowNewAddressForm(false);
    setFormData((prev) => ({
      ...prev,
      customer_name: address.name || prev.customer_name,
      customer_phone: removePhonePrefix(address.phone) || prev.customer_phone,
      delivery_name: address.name,
      delivery_phone: removePhonePrefix(address.phone),
      delivery_address_line1: address.address_line1,
      delivery_address_line2: address.address_line2 || "",
      delivery_city: address.city,
      delivery_state: address.state,
      delivery_pincode: address.pincode,
      delivery_country: address.country,
    }));
  };

  const fetchCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const response = await couponsApi.getAll();
      // Handle both wrapped and unwrapped responses
      const data = response.data || response;
      const couponsList = data.coupons || data || [];
      setAvailableCoupons(Array.isArray(couponsList) ? couponsList : []);
    } catch (error: any) {
      console.error("Failed to fetch coupons:", error);
      setAvailableCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleSelectCoupon = async (couponId: number) => {
    if (!couponId) {
      handleRemoveCoupon();
      return;
    }

    const selectedCoupon = availableCoupons.find((c) => c.id === couponId);
    if (!selectedCoupon) {
      toast.error("Coupon not found");
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const response = await couponsApi.apply(selectedCoupon.code, subtotal);
      if (response.success) {
        setSelectedCouponId(couponId);
        setAppliedCoupon(response.coupon);
        setCouponDiscount(response.discount_amount);
        toast.success(response.message || "Coupon applied successfully!");
      } else {
        toast.error(response.message || "Coupon cannot be applied");
        setSelectedCouponId(null);
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    } catch (error: any) {
      console.error("Failed to apply coupon:", error);
      toast.error(error.response?.data?.detail || "Failed to apply coupon");
      setSelectedCouponId(null);
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setSelectedCouponId(null);
    setAppliedCoupon(null);
    setCouponDiscount(0);
    toast("Coupon removed");
  };

  const getCouponDisplayText = (coupon: any) => {
    const discountText =
      coupon.discount_type === "percentage"
        ? `${coupon.discount_value}%${coupon.max_discount ? ` (Max ₹${coupon.max_discount})` : ""}`
        : `₹${coupon.discount_value}`;
    const minOrderText = coupon.min_order_amount ? ` | Min: ₹${coupon.min_order_amount}` : "";
    return `${coupon.code} - ${discountText}${minOrderText}`;
  };

  const handleSaveAddress = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    try {
      const addressData = {
        name: formData.delivery_name,
        phone: formData.delivery_phone,
        address_line1: formData.delivery_address_line1,
        address_line2: formData.delivery_address_line2 || undefined,
        city: formData.delivery_city,
        state: formData.delivery_state,
        pincode: formData.delivery_pincode,
        country: formData.delivery_country,
        is_default: savedAddresses.length === 0, // Set as default if it's the first address
      };

      const newAddress = await addressesApi.create(addressData);
      toast.success("Address saved successfully!");
      await fetchAddresses(); // Refresh addresses list
      setSelectedAddressId(newAddress.id);
      setShowNewAddressForm(false);
    } catch (error: any) {
      console.error("Failed to save address:", error);
      toast.error(error.response?.data?.detail || "Failed to save address");
    }
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.delivery_name.trim()) {
      newErrors.delivery_name = "Name is required";
    }

    if (!formData.delivery_phone.trim()) {
      newErrors.delivery_phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.delivery_phone.replace(/\D/g, ""))) {
      newErrors.delivery_phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.customer_email.trim()) {
      newErrors.customer_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      newErrors.customer_email = "Please enter a valid email address";
    }

    if (!formData.delivery_address_line1.trim()) {
      newErrors.delivery_address_line1 = "Address is required";
    }

    if (!formData.delivery_city.trim()) {
      newErrors.delivery_city = "City is required";
    }

    if (!formData.delivery_state.trim()) {
      newErrors.delivery_state = "State is required";
    }

    if (!formData.delivery_pincode.trim()) {
      newErrors.delivery_pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.delivery_pincode)) {
      newErrors.delivery_pincode = "Please enter a valid 6-digit pincode";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare checkout data
      // Destructure formData to exclude properties we want to override
      const { customer_name: _, customer_email: __, customer_phone: ___, ...restFormData } = formData;
      const checkoutData = {
        items: items.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
        total_amount: total, // Use final total including discount
        shipping: shipping,
        tax: 0,
        currency: "INR",
        payment_method: paymentMethod === "cod" ? "cod" : undefined,
        ...restFormData,
        customer_name: formData.customer_name || formData.delivery_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone || formData.delivery_phone,
        is_gift: formData.is_gift,
        gift_message: formData.is_gift ? formData.gift_message : null,
      };

      // Create order (and Razorpay order if online payment)
      const paymentOrder = await paymentsApi.checkout(checkoutData);

      // Cash on Delivery: no Razorpay, redirect to success
      if (paymentMethod === "cod" || paymentOrder.payment_method === "cash_on_delivery" || !paymentOrder.razorpay_order_id) {
        setPaymentCompleted(true);
        toast.success("Order placed successfully! Pay when your order is delivered.");
        navigate(`/payment-success?order_id=${paymentOrder.order_id}&order_number=${paymentOrder.order_number || ""}`, { replace: true });
        setTimeout(() => clearCart(), 100);
        setIsProcessing(false);
        return;
      }

      // Initialize Razorpay checkout
      const options = {
        key: paymentOrder.key_id,
        amount: Math.round(paymentOrder.amount * 100), // Convert to paise
        currency: paymentOrder.currency,
        name: "Aarsa",
        description: `Order #${paymentOrder.order_id}`,
        order_id: paymentOrder.razorpay_order_id,
        handler: async function (response: any) {
          try {
            // Verify payment
            const verification = await paymentsApi.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: paymentOrder.order_id,
            });

            if (verification.success) {
              // Set payment completed flag to prevent redirect
              setPaymentCompleted(true);
              // Show success message briefly
              toast.success("Payment successful! Your order has been placed.");
              // Navigate FIRST before clearing cart to prevent redirect race condition
              navigate(`/payment-success?order_id=${paymentOrder.order_id}&order_number=${paymentOrder.order_number || ''}`, { replace: true });
              // Clear cart after navigation (use setTimeout to ensure navigation completes first)
              setTimeout(() => {
                clearCart();
              }, 100);
            } else {
              toast.error(verification.message || "Payment verification failed");
            }
          } catch (error: any) {
            console.error("Payment verification error:", error);
            toast.error(error.response?.data?.detail || "Payment verification failed");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: formData.delivery_name,
          email: user?.email || "",
          contact: formData.delivery_phone,
        },
        theme: {
          color: "#000000",
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            toast("Payment cancelled");
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setIsProcessing(false);
    }
  };

  if (items.length === 0 || !token || !user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-grow pt-20 pb-16 pb-32 md:pb-16">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Link
              to="/cart"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Cart
            </Link>
            <h1 className="text-3xl font-semibold font-playfair text-gray-900">Checkout</h1>
            <p className="text-sm text-gray-500 mt-1">Review your order and complete payment</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Delivery Details & Order Summary */}
            <div className="lg:col-span-2 space-y-6">
              {/* Delivery Address Form */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-900" />
                    <h2 className="text-xl font-semibold text-gray-900">Delivery Address</h2>
                  </div>
                  {savedAddresses.length > 0 && !showNewAddressForm && (
                    <button
                      onClick={() => {
                        setShowNewAddressForm(true);
                        setSelectedAddressId(null);
                      }}
                      className="flex items-center gap-1.5 text-sm text-gray-900 hover:text-gray-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add New Address
                    </button>
                  )}
                </div>

                {/* Saved Addresses */}
                {savedAddresses.length > 0 && !showNewAddressForm && (
                  <div className="space-y-3 mb-6">
                    {savedAddresses.map((address) => (
                      <div
                        key={address.id}
                        onClick={() => handleSelectAddress(address)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedAddressId === address.id
                            ? "border-gray-900 bg-gray-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-medium text-gray-900">{address.name}</p>
                              {address.is_default && (
                                <span className="text-xs bg-gray-900 text-white px-2 py-0.5 rounded">Default</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{address.phone}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {address.address_line1}
                              {address.address_line2 && `, ${address.address_line2}`}
                            </p>
                            <p className="text-sm text-gray-600">
                              {address.city}, {address.state} - {address.pincode}
                            </p>
                            <p className="text-sm text-gray-600">{address.country}</p>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {selectedAddressId === address.id && (
                              <div className="w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                            {selectedAddressId !== address.id && (
                              <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Address Form */}
                {(showNewAddressForm || savedAddresses.length === 0) && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="delivery_name"
                          value={formData.delivery_name}
                          onChange={(e) => {
                            handleInputChange(e);
                            // Also update customer_name
                            setFormData((prev) => ({
                              ...prev,
                              customer_name: e.target.value,
                            }));
                          }}
                          className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                            errors.delivery_name ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="Enter your full name"
                        />
                      </div>
                      {errors.delivery_name && (
                        <p className="text-xs text-red-500 mt-1">{errors.delivery_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          name="delivery_phone"
                          value={formData.delivery_phone}
                          onChange={(e) => {
                            handleInputChange(e);
                            // Also update customer_phone
                            setFormData((prev) => ({
                              ...prev,
                              customer_phone: e.target.value,
                            }));
                          }}
                          className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                            errors.delivery_phone ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="10-digit mobile number"
                          maxLength={10}
                        />
                      </div>
                      {errors.delivery_phone && (
                        <p className="text-xs text-red-500 mt-1">{errors.delivery_phone}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                          errors.customer_email ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="your.email@example.com"
                      />
                    </div>
                    {errors.customer_email && (
                      <p className="text-xs text-red-500 mt-1">{errors.customer_email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="delivery_address_line1"
                      value={formData.delivery_address_line1}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                        errors.delivery_address_line1 ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="House/Flat No., Building Name"
                    />
                    {errors.delivery_address_line1 && (
                      <p className="text-xs text-red-500 mt-1">{errors.delivery_address_line1}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Address Line 2 (Optional)
                    </label>
                    <input
                      type="text"
                      name="delivery_address_line2"
                      value={formData.delivery_address_line2}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Street, Area, Landmark"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="delivery_city"
                        value={formData.delivery_city}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                          errors.delivery_city ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="City"
                      />
                      {errors.delivery_city && (
                        <p className="text-xs text-red-500 mt-1">{errors.delivery_city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="delivery_state"
                        value={formData.delivery_state}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                          errors.delivery_state ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="State"
                      />
                      {errors.delivery_state && (
                        <p className="text-xs text-red-500 mt-1">{errors.delivery_state}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="delivery_pincode"
                        value={formData.delivery_pincode}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                          errors.delivery_pincode ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                      {errors.delivery_pincode && (
                        <p className="text-xs text-red-500 mt-1">{errors.delivery_pincode}</p>
                      )}
                    </div>
                  </div>
                  {savedAddresses.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleSaveAddress}
                        className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium"
                      >
                        Save Address
                      </button>
                      <button
                        onClick={() => {
                          setShowNewAddressForm(false);
                          const defaultAddress = savedAddresses.find((addr: any) => addr.is_default) || savedAddresses[0];
                          if (defaultAddress) {
                            handleSelectAddress(defaultAddress);
                          }
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  </div>
                )}
              </motion.div>

              {/* Gift Options */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="w-5 h-5 text-gray-900" />
                  <h2 className="text-xl font-semibold text-gray-900">Gift Options</h2>
                </div>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.is_gift}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          is_gift: e.target.checked,
                          gift_message: e.target.checked ? prev.gift_message : "", // Clear message if unchecked
                        }));
                      }}
                      className="w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-2 focus:ring-gray-900 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                      Mark this purchase as a gift
                    </span>
                  </label>
                  {formData.is_gift && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gift Message (Optional)
                      </label>
                      <textarea
                        name="gift_message"
                        value={formData.gift_message}
                        onChange={handleInputChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                        placeholder="Write a personal message to include with the gift..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.gift_message.length}/500 characters
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-gray-900" />
                  <h2 className="text-xl font-semibold text-gray-900">Payment Method</h2>
                </div>
                <div className="space-y-3">
                  <div
                    onClick={() => setPaymentMethod("razorpay")}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "razorpay"
                        ? "border-gray-900 bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        paymentMethod === "razorpay" ? "bg-gray-900" : "bg-gray-200"
                      }`}>
                        <CreditCard className={`w-6 h-6 ${paymentMethod === "razorpay" ? "text-white" : "text-gray-600"}`} />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <p className="font-medium text-gray-900">Razorpay Secure Payment</p>
                      <p className="text-sm text-gray-500">Pay via Credit/Debit Card, UPI, Net Banking, or Wallets</p>
                    </div>
                    {paymentMethod === "razorpay" && (
                      <div className="w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => setPaymentMethod("cod")}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "cod"
                        ? "border-gray-900 bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        paymentMethod === "cod" ? "bg-gray-900" : "bg-gray-200"
                      }`}>
                        <Banknote className={`w-6 h-6 ${paymentMethod === "cod" ? "text-white" : "text-gray-600"}`} />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <p className="font-medium text-gray-900">Cash on Delivery (COD)</p>
                      <p className="text-sm text-gray-500">Pay in cash when your order is delivered</p>
                    </div>
                    {paymentMethod === "cod" && (
                      <div className="w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-6 sticky top-20"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>

                {/* Order Items */}
                <div className="space-y-3 mb-6 pb-6 border-b border-gray-200 max-h-64 overflow-y-auto">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <Link
                        to={`/products/${item.slug || item.productId}`}
                        className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-gray-100"
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/product1.png";
                          }}
                        />
                      </Link>
                      <div className="flex-grow min-w-0">
                        <Link
                          to={`/products/${item.slug || item.productId}`}
                          className="font-medium text-sm text-gray-900 hover:text-gray-700 line-clamp-2"
                        >
                          {item.name}
                        </Link>
                        <p className="text-xs text-gray-500 mt-0.5">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Coupon Section */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-gray-600" />
                    <h3 className="text-sm font-medium text-gray-900">Apply Coupon</h3>
                  </div>
                  
                  {loadingCoupons ? (
                    <div className="text-sm text-gray-500">Loading coupons...</div>
                  ) : availableCoupons.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No applicable coupons available</div>
                  ) : (
                    <div className="space-y-3">
                      <CustomDropdown
                        value={selectedCouponId ? selectedCouponId.toString() : ""}
                        onChange={(value) => {
                          const couponId = value ? parseInt(value) : null;
                          if (couponId) {
                            handleSelectCoupon(couponId);
                          } else {
                            handleRemoveCoupon();
                          }
                        }}
                        options={[
                          { value: "", label: "Select a coupon..." },
                          ...availableCoupons.map((coupon) => ({
                            value: coupon.id.toString(),
                            label: getCouponDisplayText(coupon),
                          })),
                        ]}
                        placeholder="Select a coupon..."
                        className="w-full"
                        size="md"
                        disabled={isApplyingCoupon}
                      />
                      
                      {appliedCoupon && (
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-900">
                              {appliedCoupon.code} applied
                            </span>
                          </div>
                          <button
                            onClick={handleRemoveCoupon}
                            className="text-green-600 hover:text-green-700 transition-colors"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">₹{subtotal.toLocaleString()}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Discount ({appliedCoupon.code})</span>
                      <span className="font-medium text-green-600">-₹{couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5" />
                      Shipping
                    </span>
                    <span className="font-medium">
                      {shipping === 0 ? (
                        <span className="text-green-600 text-xs">Free</span>
                      ) : (
                        `₹${shipping.toLocaleString()}`
                      )}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-semibold text-gray-900">Total</span>
                      <span className="text-xl font-semibold text-gray-900">₹{total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Place Order Button - Desktop Only */}
                <div className="hidden md:block">
                  <button
                    onClick={handlePlaceOrder}
                    disabled={isProcessing}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : paymentMethod === "cod" ? (
                      <>
                        <Lock className="w-4 h-4" />
                        Place Order
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        Place Order & Pay
                      </>
                    )}
                  </button>
                </div>

                {/* Security Badges */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      <span>Secure</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      <span>Encrypted</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed Bottom Button - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-lg">
        <div className="p-4">
          <button
            onClick={handlePlaceOrder}
            disabled={isProcessing}
            className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : paymentMethod === "cod" ? (
              <>
                <Lock className="w-4 h-4" />
                Place Order
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Place Order & Pay
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

